import React, { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const AvailabilityManager = ({ userId, userName }) => {
  const [availability, setAvailability] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const daysOfWeek = [
    { value: 'monday', label: 'Montag' },
    { value: 'tuesday', label: 'Dienstag' },
    { value: 'wednesday', label: 'Mittwoch' },
    { value: 'thursday', label: 'Donnerstag' },
    { value: 'friday', label: 'Freitag' },
    { value: 'saturday', label: 'Samstag' },
    { value: 'sunday', label: 'Sonntag' }
  ];

  const [slots, setSlots] = useState({
    monday: [{ start_time: '09:00', end_time: '17:00' }],
    tuesday: [{ start_time: '09:00', end_time: '17:00' }],
    wednesday: [{ start_time: '09:00', end_time: '17:00' }],
    thursday: [{ start_time: '09:00', end_time: '17:00' }],
    friday: [{ start_time: '09:00', end_time: '17:00' }],
    saturday: [],
    sunday: []
  });

  useEffect(() => {
    fetchAvailability();
  }, [userId]);

  const fetchAvailability = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${BACKEND_URL}/api/appointments/availability/${userId}`,
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
      );
      
      if (response.data && response.data.availability_slots) {
        setAvailability(response.data);
        // Convert availability_slots to our slots format
        const newSlots = {
          monday: [],
          tuesday: [],
          wednesday: [],
          thursday: [],
          friday: [],
          saturday: [],
          sunday: []
        };
        response.data.availability_slots.forEach(slot => {
          newSlots[slot.day_of_week] = slot.time_slots;
        });
        setSlots(newSlots);
      }
    } catch (error) {
      console.error('Error fetching availability:', error);
      // If 404 or no availability, that's fine for first time setup
      if (error.response?.status !== 404) {
        setError('Fehler beim Laden der Verfügbarkeiten');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTimeChange = (day, slotIndex, field, value) => {
    setSlots(prev => ({
      ...prev,
      [day]: prev[day].map((slot, i) => 
        i === slotIndex ? { ...slot, [field]: value } : slot
      )
    }));
  };

  const addTimeSlot = (day) => {
    setSlots(prev => ({
      ...prev,
      [day]: [...prev[day], { start_time: '09:00', end_time: '17:00' }]
    }));
  };

  const removeTimeSlot = (day, slotIndex) => {
    setSlots(prev => ({
      ...prev,
      [day]: prev[day].filter((_, i) => i !== slotIndex)
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(false);

      // Convert slots to API format
      const availability_slots = [];
      Object.keys(slots).forEach(day => {
        if (slots[day].length > 0) {
          availability_slots.push({
            day_of_week: day,
            time_slots: slots[day]
          });
        }
      });

      await axios.post(
        `${BACKEND_URL}/api/appointments/availability`,
        {
          vertriebsleiter_id: userId,
          availability_slots
        },
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
      );

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error('Error saving availability:', error);
      
      // Handle different error formats
      let errorMessage = 'Fehler beim Speichern';
      
      if (error.response?.data) {
        const errorData = error.response.data;
        
        // Handle FastAPI validation errors (array of error objects)
        if (Array.isArray(errorData.detail)) {
          errorMessage = errorData.detail.map(err => {
            const field = err.loc ? err.loc.join(' -> ') : 'Unbekannt';
            return `${field}: ${err.msg}`;
          }).join(', ');
        } 
        // Handle simple string error
        else if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        }
        // Handle object error
        else if (typeof errorData.detail === 'object') {
          errorMessage = JSON.stringify(errorData.detail);
        }
      }
      
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-lg p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Verfügbarkeiten verwalten</h2>
        <p className="text-gray-600">Legen Sie Ihre Verfügbarkeiten für Abschlussgespräche fest</p>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800">
          {error}
        </div>
      )}

      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-800">
          ✅ Verfügbarkeiten erfolgreich gespeichert!
        </div>
      )}

      <div className="space-y-6">
        {daysOfWeek.map(({ value, label }) => (
          <div key={value} className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">{label}</h3>
              <button
                onClick={() => addTimeSlot(value)}
                className="text-blue-600 hover:text-blue-700 text-sm font-medium"
              >
                + Zeitfenster hinzufügen
              </button>
            </div>

            {slots[value].length === 0 ? (
              <p className="text-gray-500 text-sm">Keine Verfügbarkeit</p>
            ) : (
              <div className="space-y-3">
                {slots[value].map((slot, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <input
                      type="time"
                      value={slot.start_time}
                      onChange={(e) => handleTimeChange(value, index, 'start_time', e.target.value)}
                      className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <span className="text-gray-600">bis</span>
                    <input
                      type="time"
                      value={slot.end_time}
                      onChange={(e) => handleTimeChange(value, index, 'end_time', e.target.value)}
                      className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    {slots[value].length > 1 && (
                      <button
                        onClick={() => removeTimeSlot(value, index)}
                        className="text-red-600 hover:text-red-700 p-2"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="mt-6 flex justify-end space-x-3">
        <button
          onClick={fetchAvailability}
          className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
        >
          Zurücksetzen
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? 'Speichern...' : 'Verfügbarkeiten speichern'}
        </button>
      </div>
    </div>
  );
};

export default AvailabilityManager;